import type {AvailableFilters, ColumnFiltersState, SortingState} from '@linx-security/table';
import type {Filter} from 'mongodb';

import {transformFilters} from './filters';
import {transformSort} from './sort';

export type APIUrlOptions = {
  baseUrl: string;
  sort?: SortingState;
  filters?: ColumnFiltersState;
  availableFilters?: AvailableFilters;
};

export class APIUrl extends URL {
  static prefix = `/api`;
  #sort: SortingState = [];
  #filters: ColumnFiltersState = [];
  #availableFilters: AvailableFilters = {};

  set baseUrl(value: string) {
    try {
      const next = new URL(value);
      this.protocol = next.protocol;
      this.host = next.host;
      this.port = next.port;
      this.hostname = next.hostname;
    } catch {
      console.error(`Invalid URL: ${value}`);
    }
  }

  // In the constructor we define the initial state of the URL
  constructor({baseUrl, sort, availableFilters, filters}: APIUrlOptions) {
    super(baseUrl);
    sort && (this.#sort = sort);
    filters && (this.#filters = filters);
    availableFilters && (this.#availableFilters = availableFilters);
  }

  sortBy(sort: SortingState) {
    this.#sort = sort;
    return this;
  }

  filterBy(filters: ColumnFiltersState) {
    this.#filters = filters;
    return this;
  }

  setBaseUrl(value: string) {
    this.baseUrl = value;
    return this;
  }

  setPathname(value: string) {
    this.pathname = `${APIUrl.prefix}${value}`;
    return this;
  }

  setParams(params: Record<string, string>) {
    Object.entries(params).forEach(([key, value]) => {
      this.searchParams.set(key, value);
    });
    return this;
  }

  asString() {
    if (this.#sort.length > 0) {
      const value = transformSort(this.#sort).join(`,`);
      this.searchParams.set(`sort`, value);
    }

    if (this.#filters.length > 0) {
      const asMongoFilter = transformFilters({params: this.#filters, availableFilters: this.#availableFilters});
      const value = JSON.stringify(asMongoFilter);
      this.searchParams.set(`filters`, value);
    }

    return super.toString();
  }
}

export type ExportURLOptions = APIUrlOptions & {
  /**
   * The format to export the data as
   * @default 'csv'
   */
  as?: 'csv' | 'json';
  [key: string]: any;
};
// TODO: support more exporting features
export class ExportURL extends APIUrl {
  #as: 'csv' | 'json' = `csv`;
  constructor({as, ...options}: ExportURLOptions) {
    super(options);
    as && (this.#as = as);
  }
}

// Example for export URL with generics and MongoDB filter
/**
 * TypedAPIUrl is a utility class that extends URL and allows you to filter the URL with a MongoDB filter (which is the interface the BE uses to filter data)
 * It's leveres generics to type the filter and the URL statically
 * @example
 * const url = new TypedAPIUrl<User>(`http://example.com`);
 * v.filterBy({$and: [{id: {$eq: `1`}}, {app_name: {$elemMatch: [`GITHUB`]}}]});
 *
 */
class _TypedAPIUrl<T> extends URL {
  #filter: Filter<T> = {};

  filterBy(filter: Filter<T>) {
    this.#filter = filter;
  }

  asString() {
    if (this.#filter) {
      const value = JSON.stringify(this.#filter);
      this.searchParams.set(`filter`, value);
    }

    return super.toString();
  }
}
